import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Combobox } from "@/components/ui/combobox";
import { ContractTypeOptions } from "@/lib/api/otc";
import { OrderType, OtcMode, REOWN_CONTRACT_TYPES } from "./types";

const HEDGEABLE_OPTIONS = ContractTypeOptions.filter((o) => !REOWN_CONTRACT_TYPES.has(o.value));
const REOWN_OPTIONS = ContractTypeOptions.filter((o) => REOWN_CONTRACT_TYPES.has(o.value));

interface OrderTypeFieldProps {
  orderType: OrderType;
  otcMode: OtcMode | null;
  otcContractType: string;
  errorOrderType?: string;
  errorOtcContractType?: string;
  suggestionOrderType?: OrderType;
  showOrderTypeSuggestion: boolean;
  onChangeOrderType: (v: OrderType) => void;
  onChangeOtcMode: (v: OtcMode) => void;
  onChangeOtcContractType: (v: string) => void;
  onApplyOrderTypeSuggestion: () => void;
}

const ORDER_TYPE_LABELS: Record<OrderType, string> = {
  none: "None",
  hta: "HTA",
  pricing: "Pricing",
  position_adjust: "Position Adjust",
  cash: "Cash",
  otc: "OTC",
};

export function OrderTypeField({
  orderType,
  otcMode,
  otcContractType,
  errorOrderType,
  errorOtcContractType,
  suggestionOrderType,
  showOrderTypeSuggestion,
  onChangeOrderType,
  onChangeOtcMode,
  onChangeOtcContractType,
  onApplyOrderTypeSuggestion,
}: OrderTypeFieldProps) {
  const availableOtcContractTypes =
    otcMode === "reown" ? REOWN_OPTIONS : otcMode === "hedgeable" ? HEDGEABLE_OPTIONS : [];

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <Label>
          Order Type <span className="text-destructive">*</span>
        </Label>
        {showOrderTypeSuggestion && suggestionOrderType && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onApplyOrderTypeSuggestion}
            className="text-xs h-7"
          >
            Apply suggestion: {ORDER_TYPE_LABELS[suggestionOrderType]}
          </Button>
        )}
      </div>

      <RadioGroup
        value={orderType}
        onValueChange={(v) => onChangeOrderType(v as OrderType)}
        className="flex flex-wrap gap-x-4 gap-y-1"
      >
        {(Object.keys(ORDER_TYPE_LABELS) as OrderType[]).map((type) => (
          <div key={type} className="flex items-center space-x-2">
            <RadioGroupItem value={type} id={`ttv2-type-${type}`} />
            <Label htmlFor={`ttv2-type-${type}`} className="font-normal cursor-pointer">
              {ORDER_TYPE_LABELS[type]}
            </Label>
          </div>
        ))}
      </RadioGroup>

      {errorOrderType && <p className="text-destructive text-xs">{errorOrderType}</p>}

      {orderType === "otc" && (
        <div className="space-y-3 pl-4 border-l-2 border-muted">
          <div className="flex gap-2">
            <Button
              type="button"
              variant={otcMode === "hedgeable" ? "default" : "outline"}
              size="sm"
              onClick={() => onChangeOtcMode("hedgeable")}
            >
              Hedgeable
            </Button>
            <Button
              type="button"
              variant={otcMode === "reown" ? "default" : "outline"}
              size="sm"
              onClick={() => onChangeOtcMode("reown")}
            >
              Reown
            </Button>
          </div>

          {otcMode !== null && (
            <div className="space-y-1">
              <Label>
                OTC Contract Type <span className="text-destructive">*</span>
              </Label>
              <Combobox
                dwimOnChange
                isModal={false}
                placeholder="Select contract type..."
                options={availableOtcContractTypes}
                value={otcContractType}
                onValueChange={onChangeOtcContractType}
                popoverClassName="w-[300px]"
              />
              {errorOtcContractType && (
                <p className="text-destructive text-xs">{errorOtcContractType}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
